/* eslint-disable max-len */
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { CitationBadge } from './Citation.styles';
import { CitationProps } from './Citation.types';
import { useCitation } from './CitationContext';
import { AllieFileIcon } from '../../../styling/icons/Icons';
import { AllieEllipsedText } from '../EllipsedText';

export const Citation: React.FC<CitationProps> = ({ citationId, onClick, labels }) => {
  const { citations, openCitation, labels: contextLabels } = useCitation();
  const badgeRef = useRef<HTMLSpanElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [portalContainer] = useState(() => {
    // Crear contenedor para el portal una sola vez
    const container = document.createElement('div');
    container.style.cssText = 'position: fixed; top: 0; left: 0; pointer-events: none; z-index: 10000;';
    return container;
  });

  // Comparación que funciona con number o string
  const citation = citations.find((c) => String(c.segment_id) === String(citationId));

  // Use provided labels or context labels
  const finalLabels = labels || contextLabels;

  // Agregar/remover contenedor del portal
  useEffect(() => {
    document.body.appendChild(portalContainer);
    return () => {
      document.body.removeChild(portalContainer);
    };
  }, [portalContainer]);

  // Función para actualizar posición del texto
  const updateTextPosition = useCallback(() => {
    if (badgeRef.current && textRef.current) {
      const rect = badgeRef.current.getBoundingClientRect();
      
      // Posicionar el texto flotante justo después del badge con solapamiento de 12px
      const leftPosition = rect.right - 9;
      const topPosition = rect.top;
      
      textRef.current.style.left = `${leftPosition}px`;
      textRef.current.style.top = `${topPosition}px`;
    }
  }, []);

  // Actualizar posición cuando se hace hover
  useEffect(() => {
    if (isHovered) {
      updateTextPosition();
      
      // Actualizar en scroll y resize
      const handleScroll = () => updateTextPosition();
      const handleResize = () => updateTextPosition();
      
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      
      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isHovered, updateTextPosition]);

  if (!citation) {
    return null;
  }

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
    openCitation(citationId);
  };

  // Extract filename from URI
  const filename = citation.title || citation.uri.split('/').pop();

  return (
    <>
      <CitationBadge
        ref={badgeRef}
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        role="button"
        tabIndex={0}
        aria-label={`Ver referencia ${citationId}`}
        data-testid={`citation-badge-${citationId}`}
      >
        <span style={{ marginBottom: -4 }}><AllieFileIcon size="12" /></span>
      </CitationBadge>
      {isHovered && createPortal(
        <span 
          ref={textRef}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={() => {handleClick(); setIsHovered(false);}}
          style={{
            cursor: 'pointer',
            position: 'fixed',
            opacity: 1,
            pointerEvents: 'auto',
            whiteSpace: 'nowrap',
            backgroundColor: 'var(--allie-color-neutral-20)',
            padding: '2.5px 8px 2.5px 4px',
            borderRadius: '0 42px 42px 0',
            display: 'flex',
            alignItems: 'center',
            zIndex: 10000,
            height: 'fit-content',
          }}
        >
          <AllieEllipsedText
            value={filename || ''}
            maxWidth="200px"
            triggerId="citation-filename"
            triggerAction="hover"
            textProps={{
              type: 'field3',
              color: 'neutral-80',
              weight: 400,
            }}
            tooltipEnabled={false}
          />
        </span>,
        portalContainer
      )}
    </>
  );
};

Citation.displayName = 'Citation';
